import "./i18n/config";
import createCache, { type EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ModalManager } from "@pepperstone/pepper-ui-kit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setupClients } from "@/api";
import { initApplePay } from "@/api/apple-pay";
import { initGooglePaySDKInstance } from "@/api/google-pay";
import { initBraintree } from "@/api/paypal";
import { getNuveiSafeChargeSDKInstance } from "@/api/safeCharge";
import { injectThreeDSChallengeStyles } from "@/api/safeCharge/threeDSChallengeStyles";
import { Toast } from "@/components/toast/Toast.tsx";
import { initDatadogRum } from "@/observability/datadog";
import { logger } from "@/observability/logger";
import { CustomerDataProvider } from "@/providers/CustomerDataContext";
import { FeatureFlagGuard } from "@/providers/FeatureFlagGuard";
import { StoreProvider } from "@/providers/StoreProvider";
import { Router } from "@/router/router";
import { serializeError } from "@/utils/log";
import { SkeletonLoadingScreen } from "@/components/skeleton-loading-screen/SkeletonLoadingScreen";

export type AppProps = {
  token?: string;
  locale: string;
};

const queryClient = new QueryClient();

// Datadog RUM first, so errors from the PSP SDK initialisers below are captured.
// Idempotent across Qiankun remounts; no-op when the env has no RUM app provisioned.
initDatadogRum();

initGooglePaySDKInstance({
  environment: import.meta.env.VITE_GOOGLE_PAY_ENV,
})
  .then((instance) => {
    window.googlePayInstance = instance;
  })
  .catch((error) => {
    logger.error("Error initializing Google Pay SDK:", {
      error: serializeError(error),
    });
  });

// Brand the Nuvei-rendered 3DS challenge modal to match our dialog. The SDK
// appends its own modal to the host light DOM, so the styles must live in
// document.head (outside our shadow root). Idempotent.
injectThreeDSChallengeStyles();

getNuveiSafeChargeSDKInstance({
  env: import.meta.env.VITE_NUVEI_ENV,
})
  .then((sdk) => {
    console.debug("SafeCharge SDK initialized.");
    window.safeChargeSdkInstance = sdk;
    return initApplePay();
  })
  .then(() => {
    console.debug("Apple Pay (Nuvei SDK) scripts loaded.");
  })
  .catch((error) => {
    logger.error("Error initializing SafeCharge / Apple Pay:", {
      error: serializeError(error),
    });
  });

initBraintree()
  .then(() => {
    console.debug("Braintree (PayPal) scripts loaded.");
  })
  .catch((error) => {
    logger.error("Error loading Braintree scripts:", {
      error: serializeError(error),
    });
  });

export const App = ({ token, locale }: AppProps) => {
  const { i18n } = useTranslation();
  // Normalize locale: if the exact locale isn't supported (e.g. "en-af" where "af"
  // is a licence suffix, or "es-419"), strip to the base language tag. This prevents
  // an infinite re-render loop caused by calling changeLanguage() with an unsupported
  // locale that i18next normalises to a different value on every render.
  const supportedLngs = Array.isArray(i18n.options.supportedLngs)
    ? (i18n.options.supportedLngs as string[])
    : ["en"];
  const normalizedLocale = supportedLngs.includes(locale)
    ? locale
    : supportedLngs.includes(locale.split("-")[0])
      ? locale.split("-")[0]
      : "en";

  useEffect(() => {
    if (normalizedLocale !== i18n.language) {
      i18n.changeLanguage(normalizedLocale);
    }
  }, [normalizedLocale, i18n]);

  // Emotion Cache for react-select components
  const [cache, setCache] = useState<EmotionCache | null>(null);
  const containerRef = (node: HTMLElement | null) => {
    if (node && !cache) {
      setCache(
        createCache({
          key: "pnx-cashier-emotion-cache",
          container: node,
        })
      );
    }
  };

  useEffect(() => {
    if (token) {
      setupClients({ accessToken: token });
    }
  }, [token]);

  if (!token) {
    console.debug("Waiting for token...");
    return (
      <section className="cashier">
        <div className="cashier-mfe-wrapper">
          <SkeletonLoadingScreen />
        </div>
      </section>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <section className="cashier" ref={containerRef}>
        <CacheProvider value={cache}>
          <CustomerDataProvider>
            <FeatureFlagGuard>
              <StoreProvider>
                <ModalManager>
                  <Router />
                </ModalManager>
                <Toast />
              </StoreProvider>
            </FeatureFlagGuard>
          </CustomerDataProvider>
        </CacheProvider>
      </section>
    </QueryClientProvider>
  );
};
